import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { reviewService } from '../../services/api';
import styles from './ProductReviews.module.css';

const ProductReviews = ({ productId, productRating, productReviewCount }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: ''
  });

  // Utiliser un ref pour stocker la note actuelle et éviter les problèmes de closure
  const currentRatingRef = useRef(5);

  // Mettre à jour le ref quand formData.rating change
  useEffect(() => {
    currentRatingRef.current = formData.rating;
  }, [formData.rating]);

  const [submitting, setSubmitting] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { addNotification } = useNotifications();

  useEffect(() => {
    if (productId) {
      loadReviews();
    }
  }, [productId]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await reviewService.getProductReviews(productId);

      if (response.success) {
        // La réponse peut être directement un tableau ou dans response.data
        let reviewsData = [];

        if (Array.isArray(response.data)) {
          reviewsData = response.data;
        } else if (response.data?.data && Array.isArray(response.data.data)) {
          reviewsData = response.data.data;
        } else if (response.data?.reviews && Array.isArray(response.data.reviews)) {
          reviewsData = response.data.reviews;
        } else if (response.data && typeof response.data === 'object') {
          // Si response.data est un objet avec une propriété reviews
          reviewsData = response.data.reviews || [];
        }

        // S'assurer que les notes sont des nombres
        const normalizedReviews = reviewsData.map(review => ({
          ...review,
          rating: parseInt(review.rating) || 0
        }));

        setReviews(normalizedReviews);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des avis:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      addNotification('Vous devez être connecté pour laisser un avis', 'warning');
      return;
    }

    // Valider que la note est entre 1 et 5
    if (!formData.rating || formData.rating < 1 || formData.rating > 5) {
      addNotification('Veuillez sélectionner une note entre 1 et 5 étoiles', 'warning');
      return;
    }

    try {
      setSubmitting(true);

      // S'assurer que la note est bien un entier entre 1 et 5
      const ratingValue = parseInt(formData.rating);
      if (isNaN(ratingValue) || ratingValue < 1 || ratingValue > 5) {
        addNotification('Veuillez sélectionner une note valide entre 1 et 5 étoiles', 'warning');
        setSubmitting(false);
        return;
      }

      // Utiliser le ref pour obtenir la valeur la plus récente
      const finalRating = parseInt(currentRatingRef.current || formData.rating);

      if (isNaN(finalRating) || finalRating < 1 || finalRating > 5) {
        addNotification('Veuillez sélectionner une note valide entre 1 et 5 étoiles', 'warning');
        setSubmitting(false);
        return;
      }

      const reviewData = {
        product_id: parseInt(productId),
        rating: finalRating,
        title: formData.title?.trim() || null,
        comment: formData.comment?.trim() || null
      };

      const response = await reviewService.createReview(reviewData);

      if (response.success) {
        addNotification('Votre avis a été ajouté avec succès', 'success');
        setFormData({ rating: 5, title: '', comment: '' });
        setShowForm(false);
        await loadReviews();
        // Recharger la page pour mettre à jour le rating
        window.location.reload();
      } else {
        addNotification(response.message || 'Erreur lors de l\'ajout de l\'avis', 'error');
      }
    } catch (error) {
      addNotification(error.response?.data?.message || 'Erreur lors de l\'ajout de l\'avis', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, interactive = false, onChange = null) => {
    // Convertir la note en nombre pour s'assurer qu'elle est correcte
    const numericRating = parseInt(rating) || 0;

    const handleStarClick = (starValue, e) => {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (interactive && onChange) {
        onChange(starValue);
      }
    };

    if (interactive) {
      // Version interactive avec des boutons
      return (
        <div className={styles.stars}>
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className={`${styles.star} ${styles.starButton} ${star <= numericRating ? styles.filled : ''}`}
              onClick={(e) => handleStarClick(star, e)}
              aria-label={`Noter ${star} étoile${star > 1 ? 's' : ''}`}
            >
              ⭐
            </button>
          ))}
        </div>
      );
    }

    // Version non-interactive avec des spans
    return (
      <div className={styles.stars}>
        {[1, 2, 3, 4, 5].map((star) => {
          const isFilled = star <= numericRating;
          return (
            <span
              key={star}
              className={isFilled ? `${styles.star} ${styles.filled}` : styles.star}
              style={{
                color: isFilled ? '#FF9800' : '#ddd',
                opacity: isFilled ? 1 : 0.3
              }}
            >
              ⭐
            </span>
          );
        })}
      </div>
    );
  };

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length).toFixed(1)
    : productRating || '0';

  return (
    <div className={styles.reviewsSection}>
      <div className={styles.reviewsHeader}>
        <h3>Avis clients</h3>
        <div className={styles.ratingSummary}>
          <div className={styles.averageRating}>
            <span className={styles.ratingValue}>{averageRating}</span>
            {renderStars(Math.round(averageRating))}
            <span className={styles.reviewCount}>
              ({reviews.length || productReviewCount || 0} avis)
            </span>
          </div>
        </div>
      </div>

      {isAuthenticated && !showForm && (
        <button
          className={styles.addReviewButton}
          onClick={() => {
            // Réinitialiser le formulaire avec une note par défaut de 5
            setFormData({ rating: 5, title: '', comment: '' });
            setShowForm(true);
          }}
        >
          Laisser un avis
        </button>
      )}

      {showForm && (
        <form className={styles.reviewForm} onSubmit={handleSubmit}>
          <h4>Votre avis</h4>
          <div className={styles.formGroup}>
            <label>Note</label>
            {renderStars(formData.rating, true, (rating) => {
              const newRating = parseInt(rating);

              // Mettre à jour le ref immédiatement
              currentRatingRef.current = newRating;

              setFormData(prevData => ({
                ...prevData,
                rating: newRating
              }));
            })}
            <div style={{ marginTop: '5px', fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>
              Note actuelle: {formData.rating} / 5
            </div>
          </div>
          <div className={styles.formGroup}>
            <label>Titre (optionnel)</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Titre de votre avis"
              maxLength={255}
            />
          </div>
          <div className={styles.formGroup}>
            <label>Commentaire</label>
            <textarea
              value={formData.comment}
              onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              placeholder="Partagez votre expérience..."
              rows="4"
              maxLength={2000}
            />
          </div>
          <div className={styles.formActions}>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormData({ rating: 5, title: '', comment: '' });
              }}
              className={styles.cancelButton}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={submitting}
            >
              {submitting ? 'Envoi...' : 'Publier'}
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className={styles.loading}>Chargement des avis...</div>
      ) : reviews.length === 0 ? (
        <div className={styles.noReviews}>
          <p>Aucun avis pour le moment. Soyez le premier à laisser un avis !</p>
        </div>
      ) : (
        <div className={styles.reviewsList}>
          {reviews.map((review) => {
            const reviewRating = parseInt(review.rating) || 0;

            return (
              <div key={review.id} className={styles.reviewItem}>
                <div className={styles.reviewHeader}>
                  <div className={styles.reviewAuthor}>
                    <div className={styles.authorAvatar}>
                      {review.user?.profile?.first_name?.charAt(0) || review.user?.name?.charAt(0) || 'U'}
                    </div>
                    <div>
                      <div className={styles.authorName}>
                        {review.user?.profile?.first_name || review.user?.name || 'Utilisateur'}
                      </div>
                      <div className={styles.reviewDate}>
                        {new Date(review.created_at).toLocaleDateString('fr-FR', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </div>
                    </div>
                  </div>
                  <div className={styles.reviewRating}>
                    {renderStars(reviewRating)}
                    <span style={{ marginLeft: '10px', fontSize: '0.9rem', color: '#666' }}>
                      ({reviewRating}/5)
                    </span>
                    {review.is_verified_purchase && (
                      <span className={styles.verifiedBadge}>✓ Achat vérifié</span>
                    )}
                  </div>
                </div>
              {review.title && (
                <h5 className={styles.reviewTitle}>{review.title}</h5>
              )}
              {review.comment && (
                <p className={styles.reviewComment}>{review.comment}</p>
              )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ProductReviews;

